<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import type { ListboxItemSelectEvent } from 'reka-ui'
import { Search, CornerDownLeft } from 'lucide-vue-next'
import { Button } from '@/components/ui/button'
import Dialog from '@/components/ui/dialog/Dialog.vue'
import DialogContent from '@/components/ui/dialog/DialogContent.vue'
import Command from '@/components/ui/command/Command.vue'
import CommandEmpty from '@/components/ui/command/CommandEmpty.vue'
import CommandGroup from '@/components/ui/command/CommandGroup.vue'
import CommandInput from '@/components/ui/command/CommandInput.vue'
import CommandItem from '@/components/ui/command/CommandItem.vue'
import CommandList from '@/components/ui/command/CommandList.vue'
import { useTranslateKey } from '@/lib/utils'
import { navigateTo } from '@/router/utils'
import { type RouteName } from '@/router/routes'
import { getSidebarItems, getDropdownItems, type NavItems } from './sidebarConfig'
import { useUserStore } from '@/stores/userStore'
import type { TNKeys } from '@/components/lang/langHandler'

interface SearchItem {
  route: RouteName
  label: string
  path: string
  searchText: string
}

interface SearchConfigItem {
  label: TNKeys
  route: RouteName
  betaOnly: boolean
}

const DOUBLE_SHIFT_WINDOW_MS = 350
const isOpen = ref(false)
const lastShiftReleaseAt = ref(0)

const route = useRoute()
const router = useRouter()
const userStore = useUserStore()
const { translateKey } = useTranslateKey()

function flattenNavigationItems(items: NavItems[]): SearchConfigItem[] {
  return items.flatMap(item => {
    const current = item.route
      ? [{ label: item.label, route: item.route, betaOnly: Boolean(item.betaOnly) }]
      : []
    const nested = item.subroutes ? flattenNavigationItems(item.subroutes) : []
    return [...current, ...nested]
  })
}

const searchItems = computed<SearchItem[]>(() => {
  const sidebarItems = getSidebarItems(userStore)
  const dropdownItems = getDropdownItems(userStore)

  const navigationItems = flattenNavigationItems([
    ...sidebarItems.primary,
    ...sidebarItems.secondary,
    ...sidebarItems.admin,
    ...dropdownItems.group1,
    ...(dropdownItems.group2 ?? []),
    ...(dropdownItems.group3 ?? [])
  ]).filter(item => !item.betaOnly || userStore.isBetaAccess)

  const uniqueItems = new Map<RouteName, SearchItem>()

  for (const item of navigationItems) {
    if (uniqueItems.has(item.route)) continue

    const resolved = router.resolve({ name: item.route })
    const translatedLabel = translateKey(item.label)
    uniqueItems.set(item.route, {
      route: item.route,
      label: translatedLabel,
      path: resolved.path,
      searchText: `${translatedLabel} ${resolved.path} ${item.route}`
    })
  }

  return Array.from(uniqueItems.values()).sort((a, b) => a.label.localeCompare(b.label))
})

const openSearch = () => {
  isOpen.value = true
}

const handleItemSelect = (ev: ListboxItemSelectEvent<string>, routeName: RouteName) => {
  if (typeof ev.detail.value !== 'string') return
  isOpen.value = false
  void navigateTo(routeName)
}

const isEditableTarget = (target: EventTarget | null): boolean => {
  if (!(target instanceof HTMLElement)) return false
  if (target.isContentEditable) return true
  const tag = target.tagName
  return tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT'
}

const onWindowKeyUp = (event: KeyboardEvent) => {
  if (event.key !== 'Shift' || event.altKey || event.ctrlKey || event.metaKey) {
    return
  }
  if (isEditableTarget(event.target)) return

  const now = Date.now()
  const isDoubleShift = now - lastShiftReleaseAt.value <= DOUBLE_SHIFT_WINDOW_MS
  lastShiftReleaseAt.value = now

  if (!isDoubleShift) return

  event.preventDefault()
  openSearch()
}

const onWindowKeyDown = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || !isOpen.value) return
  event.preventDefault()
  isOpen.value = false
}

onMounted(() => {
  window.addEventListener('keydown', onWindowKeyDown)
  window.addEventListener('keyup', onWindowKeyUp)
})

onBeforeUnmount(() => {
  window.removeEventListener('keydown', onWindowKeyDown)
  window.removeEventListener('keyup', onWindowKeyUp)
})

watch(
  () => route.fullPath,
  () => {
    isOpen.value = false
  }
)
</script>

<template>
  <div class="flex items-center gap-1" data-testid="global-navigation-search">
    <Button
      variant="ghost"
      size="icon"
      class="text-muted-foreground h-9 w-9 md:hidden"
      :aria-label="translateKey('openNavigationSearch')"
      @click="openSearch"
    >
      <Search class="size-4" />
    </Button>
    <Button
      variant="ghost"
      size="sm"
      class="border-border/60 text-foreground/70 hover:text-foreground hover:bg-muted/60 hidden h-9 min-w-48 justify-start border px-3 md:inline-flex"
      @click="openSearch"
    >
      <Search class="mr-2 size-4" />
      <span class="truncate">{{ translateKey('searchButton') }}</span>
    </Button>
  </div>

  <Dialog v-model:open="isOpen">
    <DialogContent
      class="max-h-[80vh] w-[min(42rem,calc(100%-2rem))] overflow-hidden p-0"
      :overlay-class="'bg-black/35'"
    >
      <Command>
        <CommandInput :placeholder="`${translateKey('searchButton')}...`" class="h-11" />
        <CommandList class="max-h-[65vh] p-2">
          <CommandEmpty>{{ translateKey('noResultsFound') }}</CommandEmpty>
          <CommandGroup :heading="translateKey('navigation')">
            <CommandItem
              v-for="item in searchItems"
              :key="item.route"
              :value="item.searchText"
              :text-value="item.searchText"
              class="group py-2"
              @select="event => handleItemSelect(event, item.route)"
            >
              <div class="flex w-full items-center justify-between gap-4">
                <div class="min-w-0">
                  <div class="truncate">{{ item.label }}</div>
                  <div class="text-muted-foreground truncate text-xs">{{ item.path }}</div>
                </div>
                <CornerDownLeft
                  class="text-muted-foreground size-3.5 opacity-0 group-hover:opacity-100"
                />
              </div>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </Command>
    </DialogContent>
  </Dialog>
</template>
